import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDrawer } from '@core/context/DrawerContext'
import { getIssueAnalyticsProps } from '@core/modules/issues/analytics'
import { Analytics } from '@core/services/mixpanel'
import { useIssueResolution } from '@ui/drawer/common/useIssueResolution'

type TIssueStore = { useStore: () => { data: TIssue[] } }

export interface UseIssueDrawerParams<TRow extends { id: string }> {
    store: TIssueStore
    buildRows: (issue: TIssue) => TRow[]
}

export interface UseIssueDrawerResult<
    TIssueT extends TIssue,
    TRow extends { id: string },
> {
    issue: TIssueT
    rows: TRow[]
    selectedIds: string[]
    setSelectedIds: (ids: string[]) => void
    resolution: ReturnType<typeof useIssueResolution>
    closeDrawer: () => void
}

export const useIssueDrawer = <
    TIssueT extends TIssue,
    TRow extends { id: string },
>({
    store,
    buildRows,
}: UseIssueDrawerParams<TRow>): UseIssueDrawerResult<TIssueT, TRow> => {
    const { data: allIssues } = store.useStore()
    const { drawerState, setDrawerState } = useDrawer()
    const {
        issues: drawerIssues,
        index,
    }: {
        issues: TIssue[]
        index: number
    } = drawerState?.contentProps ?? {}

    const drawerIssue = drawerIssues?.[index]
    const fullIssueData =
        allIssues.find(
            (storeIssue: TIssue) => storeIssue.id === drawerIssue?.id
        ) || {}

    const issue = { ...drawerIssue, ...fullIssueData } as TIssueT

    const closeDrawer = useCallback(() => {
        setDrawerState((prevState) => ({
            ...prevState,
            isDrawerOpen: false,
        }))
    }, [setDrawerState])

    const rows = useMemo(
        () => buildRows(issue),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [drawerIssue, fullIssueData]
    )

    const [selectedIds, setSelectedIds] = useState<string[]>(() =>
        rows.map(({ id }) => id)
    )

    useEffect(() => {
        setSelectedIds(rows.map(({ id }) => id))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [issue.id])

    const issueRef = useRef(issue)
    issueRef.current = issue

    const onSelectionChange = useCallback(
        (ids: string[]) => {
            setSelectedIds(ids)

            Analytics.event('SEM::user_selects_asset_for_resolution', {
                ...getIssueAnalyticsProps(issueRef.current),
                selectedCount: ids.length,
                totalCount: rows.length,
            })
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [issue.id, issue.name, issue.status, rows.length]
    )

    const resolution = useIssueResolution({ issue, closeDrawer })

    return {
        issue,
        rows,
        selectedIds,
        setSelectedIds: onSelectionChange,
        resolution,
        closeDrawer,
    }
}
